import { getLanguage, http, location } from 'cdui-js';
import { replaceTemplate } from '@china-difi/lib/template';
import { fetchStockAccountsMeta } from '../../../api/funds';
import i18n from '../../../i18n';
import { getAuthFileBlobUrl, revokeAuthFileBlobUrl } from '../../../lib/authFileBlob';
import { appendDepositAppQuery } from '../deposit/h5-deposit/appBridge';
import {
  getMarketBankCache,
  overviewAccountsOf,
  setMarketBankCache,
  sortDepositAccounts,
  unwrapList,
} from '../deposit/h5-deposit/shared';

export { overviewAccountsOf, sortDepositAccounts, unwrapList, revokeAuthFileBlobUrl };

const quiet = (): RequestInit => ({ headers: { 'X-No-Toast': '1' } });

export type RecordKind = 'deposit' | 'withdraw' | 'exchange' | 'transfer';
export type RangeKey = 'month' | 'quarter' | 'year' | 'ytd' | 'custom';
export type StatusTone = 'pending' | 'success' | 'reject' | 'cancel' | 'muted';

const PENDING = ['PND', 'ACK', 'APV', 'BOE', 'ACP', 'BOS'];

const pad = (value: number) => String(value).padStart(2, '0');

export const formatYmd = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const startOfToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const shiftMonth = (base: Date, months: number) => {
  const next = new Date(base.getFullYear(), base.getMonth() + months, 1);
  const last = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
  next.setDate(Math.min(base.getDate(), last));
  return next;
};

const shiftYear = (base: Date, years: number) => {
  const next = new Date(base.getFullYear() + years, base.getMonth(), 1);
  const last = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
  next.setDate(Math.min(base.getDate(), last));
  return next;
};

/** 预设区间按今天重算。自定义才用弹层里选中的两个日期 */
export const rangeQuery = (key: RangeKey, customFrom = '', customTo = '') => {
  const today = startOfToday();
  const to = formatYmd(today);
  if (key === 'custom') return { from: customFrom, to: customTo };
  if (key === 'quarter') return { from: formatYmd(shiftMonth(today, -3)), to };
  if (key === 'year') return { from: formatYmd(shiftYear(today, -1)), to };
  if (key === 'ytd') return { from: `${today.getFullYear()}-01-01`, to };
  return { from: formatYmd(shiftMonth(today, -1)), to };
};

export const moneyText = (value: unknown) => {
  if (value == null || value === '') return '0.00';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '0.00';
  return amount.toFixed(2);
};

const parseTime = (value: unknown) => {
  if (value == null || value === '') return null;
  const raw = String(value).trim();
  if (/^\d+$/.test(raw)) {
    const n = Number(raw);
    const ms = n < 1e12 ? n * 1000 : n;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const match = raw.match(
    /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[ T](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?/,
  );
  if (!match) return null;
  return new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4] || 0),
    Number(match[5] || 0),
    Number(match[6] || 0),
  );
};

const formatParts = (value: unknown) => {
  const date = parseTime(value);
  if (!date) return null;
  return {
    y: date.getFullYear(),
    m: pad(date.getMonth() + 1),
    d: pad(date.getDate()),
    hh: pad(date.getHours()),
    mm: pad(date.getMinutes()),
    ss: pad(date.getSeconds()),
  };
};

export const listTimeText = (value: unknown) => {
  const parts = formatParts(value);
  if (!parts) return '--';
  return `${parts.y}/${parts.m}/${parts.d} ${parts.hh}:${parts.mm}:${parts.ss}`;
};

export const groupDateText = (value: unknown) => {
  const parts = formatParts(value);
  if (!parts) return '--';
  return `${parts.y}/${parts.m}/${parts.d}`;
};

/** 详情提交时间：与列表 `listTimeText` 同为 `YYYY/MM/DD HH:mm:ss` */
export const detailTimeText = (value: unknown) => listTimeText(value);

export const groupDeposits = (list: any[]) => {
  const groups: { date: string; rows: any[] }[] = [];
  list.forEach((row) => {
    const date = groupDateText(row?.submitTime);
    const last = groups[groups.length - 1];
    if (!last || last.date !== date) groups.push({ date, rows: [row] });
    else last.rows.push(row);
  });
  return groups;
};

export const depositStatus = (code: unknown): { text: string; tone: StatusTone } => {
  const raw = String(code ?? '').trim();
  if (!raw) return { text: '--', tone: 'muted' };
  const upper = raw.toUpperCase();
  if (PENDING.includes(upper)) return { text: i18n.FundRecords.StatusPending, tone: 'pending' };
  if (upper === 'APPROVING') return { text: i18n.FundRecords.StatusRiskReview, tone: 'pending' };
  if (upper === 'PTC') return { text: i18n.FundRecords.StatusPendingCancel, tone: 'pending' };
  if (upper === 'REJ') return { text: i18n.FundRecords.StatusRejected, tone: 'reject' };
  if (upper === 'FAILED') return { text: i18n.FundRecords.StatusFailed, tone: 'reject' };
  if (upper === 'CAN') return { text: i18n.FundRecords.StatusCancelled, tone: 'cancel' };
  if (upper === 'CLS') return { text: i18n.FundRecords.StatusSuccess, tone: 'success' };
  return { text: raw, tone: 'muted' };
};

export const recordKindOf = (value: unknown): RecordKind => {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'withdraw' || raw === 'withdrawal' || raw === 'withdrawals' || raw === 'with') return 'withdraw';
  if (raw === 'exchange') return 'exchange';
  if (raw === 'transfer' || raw === 'allocation') return 'transfer';
  if (raw === 'deposits') return 'deposit';
  return 'deposit';
};

/** 列表账户下拉。证券 type=C 才是现金，其余证券户不是现金 */
export const recordAccountLabel = (row: any) => {
  const num = String(row?.accountNum ?? '').trim();
  if (String(row?.productType ?? '').trim() === 'C') return `${i18n.FundH5.AccountVirtual}-${num}`;
  const type = String(row?.type ?? '').trim();
  if (type === 'M') return `${i18n.FundH5.AccountMargin}-${num}`;
  if (type === 'C') return `${i18n.FundH5.AccountCash}-${num}`;
  return `${i18n.FundH5.AccountStock}-${num}`;
};

export const currencyFundName = (currency: unknown) => {
  const code = String(currency ?? '').trim().toUpperCase();
  if (code === 'HKD') return i18n.FundH5.CurrencyHkdYuan;
  if (code === 'USD') return i18n.FundH5.CurrencyUsd;
  if (code === 'CNY') return i18n.FundH5.CurrencyCny;
  return code;
};

export const methodText = (method: unknown) => {
  const raw = String(method ?? '').trim();
  if (raw === 'eBanking') return i18n.FundH5.MethodBank;
  if (raw === 'Cheque') return i18n.FundH5.MethodCheque;
  return raw || '—';
};

const nonempty = (value: unknown) => {
  const text = String(value ?? '').trim();
  if (!text || text.toLowerCase() === 'null') return '';
  return text;
};

/** 去空格后长度 ≤ 4 不打码，否则最多 5 个星号加后 4 位 */
export const maskPayerAccount = (num: unknown) => {
  const text = String(num ?? '').replace(/\s+/g, '');
  if (text.length <= 4) return text;
  return `${'*'.repeat(Math.min(5, text.length - 4))}${text.slice(-4)}`;
};

const nameChain = () => {
  const lang = String(getLanguage() || 'zh-CN').trim();
  if (lang === 'en' || lang === 'en-US' || lang.startsWith('en-')) {
    return ['bankNameEN', 'bankName', 'bankNameSC', 'bankNameTC'];
  }
  if (lang === 'zh-TW' || lang === 'zh-HK' || lang === 'zh-Hant' || lang.includes('Hant')) {
    return ['bankNameTC', 'bankName', 'bankNameSC', 'bankNameEN'];
  }
  return ['bankNameSC', 'bankName', 'bankNameTC', 'bankNameEN'];
};

const dictName = (code: string) => {
  const hit = code ? getMarketBankCache().find((item) => String(item?.bankCode) === code) : null;
  if (!hit) return '';
  for (const key of nameChain()) {
    const named = nonempty(hit[key]);
    if (named) return named;
  }
  return '';
};

export const bankLine = (code: unknown, apiName: unknown, account: unknown, mask: boolean) => {
  const bankCode = nonempty(code);
  const fromDict = dictName(bankCode);
  const name = fromDict || nonempty(apiName) || bankCode;
  const shownAccount = mask ? maskPayerAccount(account) : nonempty(account);
  if (name && shownAccount) return `${name} (${shownAccount})`;
  return name || shownAccount || '—';
};

const reasonFromMessage = (raw: unknown) => {
  const text = nonempty(raw);
  if (!text) return '';
  try {
    const data = JSON.parse(text);
    if (Array.isArray(data)) {
      const hit = data.find((item) => item && item.key === 'rejectReason');
      return nonempty(hit?.message);
    }
    if (data && typeof data === 'object') return nonempty(data.rejectReason?.message);
    return '';
  } catch {
    return text;
  }
};

export const rejectReasonOf = (row: any) => {
  return nonempty(row?.channelRejectReason) || nonempty(row?.auditRejectReason) || reasonFromMessage(row?.reasonMessage);
};

export const proofPathsOf = (value: unknown) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item ?? '').trim()).filter(Boolean);
  }
  const raw = String(value ?? '').trim();
  if (!raw) return [];
  if (raw.startsWith('[')) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item ?? '').trim()).filter(Boolean);
      }
    } catch {
      // 非 JSON 数组时按逗号拆
    }
  }
  return raw
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const KIND_SEGMENTS: RecordKind[] = ['deposit', 'withdraw', 'exchange', 'transfer'];

const RECORD_ID_KEY: Record<RecordKind, string> = {
  deposit: 'depositID',
  withdraw: 'withdrawalID',
  exchange: 'exchangeID',
  transfer: 'transferID',
};

/** 解析 `/h5/fund-records/{kind}/{id}`；兼容旧入金 path */
export const recordDetailOf = (): { kind: RecordKind; id: string } | null => {
  const path = String(location.path || '');
  for (const kind of KIND_SEGMENTS) {
    const marks = [`/h5/fund-records/${kind}/`, `/fund-records/${kind}/`];
    for (const mark of marks) {
      if (!path.startsWith(mark)) continue;
      const id = decodeURIComponent(path.slice(mark.length)).split('/')[0];
      if (id) return { kind, id };
    }
  }
  return null;
};

export const depositRecordId = () => {
  const hit = recordDetailOf();
  return hit?.kind === 'deposit' ? hit.id : '';
};

const preserveFundQuery = (params: URLSearchParams) => {
  const q = (location.query || {}) as Record<string, string>;
  const accountNum = String(q.accountNum || '').trim();
  if (accountNum && !params.has('accountNum')) params.set('accountNum', accountNum);
  const mode = String(q.mode || '').trim();
  if (mode && !params.has('mode')) params.set('mode', mode);
  const modeType = String(q.modeType || '').trim();
  if (modeType && !params.has('modeType')) params.set('modeType', modeType);
  if (String(q.isBackToApp || '').trim() === '1') params.set('isBackToApp', '1');
};

export const listUrl = (kind: RecordKind = 'deposit') => {
  const params = new URLSearchParams();
  params.set('type', kind);
  preserveFundQuery(params);
  appendDepositAppQuery(params);
  return `/h5/fund-records?${params.toString()}`;
};

export const detailUrl = (kind: RecordKind, id: string, opts?: { allowCancel?: boolean }) => {
  const params = new URLSearchParams();
  preserveFundQuery(params);
  // 列表已允许撤销时带入；详情接口偶发不回 allowCancel
  if (opts?.allowCancel) params.set('allowCancel', '1');
  appendDepositAppQuery(params);
  const qs = params.toString();
  const path = `/h5/fund-records/${kind}/${encodeURIComponent(id)}`;
  return qs ? `${path}?${qs}` : path;
};

/** 接口可能回 boolean / 0|1 / "true"；缺字段时返回 null */
export const allowCancelFlag = (row: any): boolean | null => {
  const v = row?.allowCancel;
  if (v === true || v === 1 || v === '1' || v === 'true' || v === 'Y') return true;
  if (v === false || v === 0 || v === '0' || v === 'false' || v === 'N') return false;
  return null;
};

export const isAllowCancel = (row: any) => allowCancelFlag(row) === true;

const RECORD_PATH: Record<RecordKind, string> = {
  deposit: '/2go/fund/deposits',
  withdraw: '/2go/fund/withdrawals',
  exchange: '/2go/currencyExchanges',
  transfer: '/2go/fund/transfers',
};

export type RecordQuery = {
  accountNum: string;
  from: string;
  to: string;
  page: number;
  currency: string;
};

/** 四个类型 query 相同。offset 是从 1 开始的页码，不是跳过条数 */
export const fetchRecordPage = async (kind: RecordKind, query: RecordQuery) => {
  const params = new URLSearchParams({
    accountNum: query.accountNum,
    from: query.from,
    to: query.to,
    limit: '25',
    offset: String(query.page),
  });
  if (query.currency) params.set('currency', query.currency);
  return unwrapList(await http.get(`${RECORD_PATH[kind]}?${params.toString()}`, quiet()));
};

export const recordRowId = (kind: RecordKind, row: any) => {
  const key =
    kind === 'withdraw' ? 'withdrawalID' : kind === 'exchange' ? 'exchangeID' : kind === 'transfer' ? 'transferID' : 'depositID';
  return String(row?.[key] ?? '').trim();
};

/** 兑换左上用兑入金额，其余用 amount + currency */
export const recordTitle = (kind: RecordKind, row: any) => {
  if (kind === 'exchange') {
    return `${moneyText(row?.toAmount)} ${String(row?.toCurrency ?? '').trim()}`.trim();
  }
  return `${moneyText(row?.amount)} ${String(row?.currency ?? '').trim()}`.trim();
};

const moneyPair = (amount: unknown, currency: unknown) =>
  `${moneyText(amount)} ${String(currency ?? '').trim()}`.trim();

/**
 * 调拨两侧：先 AccountType（品类，C=虚拟，S=证券勿原样输出），
 * 再 CapitalType（资金性质，M=孖展 C=现金）
 */
export const accountSide = (account: unknown, accountType: unknown, capitalType: unknown) => {
  const num = String(account ?? '').trim();
  if (!num) return '—';
  const product = String(accountType ?? '').trim().toUpperCase();
  if (product === 'C') return `${i18n.FundH5.AccountVirtual}-${num}`;
  const capital = String(capitalType ?? '').trim().toUpperCase();
  if (capital === 'M') return `${i18n.FundH5.AccountMargin}-${num}`;
  if (capital === 'C') return `${i18n.FundH5.AccountCash}-${num}`;
  return num;
};

export const statusLabel = (kind: RecordKind) => {
  if (kind === 'withdraw') return i18n.FundRecords.StatusLabelWithdraw;
  if (kind === 'exchange') return i18n.FundRecords.StatusLabelExchange;
  if (kind === 'transfer') return i18n.FundRecords.StatusLabelTransfer;
  return i18n.FundRecords.StatusLabelDeposit;
};

export type RecordLine = { label: string; value: string; status?: boolean };

export const recordLines = (kind: RecordKind, row: any): RecordLine[] => {
  const time = { label: i18n.FundRecords.LabelTime, value: listTimeText(row?.submitTime) };
  const status = { label: statusLabel(kind), value: '', status: true as const };
  if (kind === 'exchange') {
    return [
      status,
      { label: i18n.FundRecords.LabelFrom, value: moneyPair(row?.fromAmount, row?.fromCurrency) },
      { label: i18n.FundRecords.LabelTo, value: moneyPair(row?.toAmount, row?.toCurrency) },
      time,
    ];
  }
  if (kind === 'transfer') {
    return [
      status,
      {
        label: i18n.FundRecords.LabelFromAccount,
        value: accountSide(row?.fromAccount, row?.fromAccountType, row?.fromAccountCapitalType),
      },
      {
        label: i18n.FundRecords.LabelToAccount,
        value: accountSide(row?.toAccount, row?.toAccountType, row?.toAccountCapitalType),
      },
      time,
    ];
  }
  return [status, time];
};

export const fetchRecordAccounts = async () =>
  sortDepositAccounts(overviewAccountsOf(await fetchStockAccountsMeta(undefined, quiet())));

export const fetchRecordDetail = (kind: RecordKind, id: string) =>
  http.get(`${RECORD_PATH[kind]}/${encodeURIComponent(id)}`);

export const fetchDepositDetail = (id: string) => fetchRecordDetail('deposit', id);

export const unwrapRecordDetail = (res: any, kind: RecordKind) => {
  if (res == null) return null;
  const key = RECORD_ID_KEY[kind];
  if (res[key] != null || res.accountNum != null) return res;
  const data = res.data;
  if (data && (data[key] != null || data.accountNum != null)) return data;
  return data || res;
};

export const recordIdKey = (kind: RecordKind) => RECORD_ID_KEY[kind];

export const cancelDeposit = (id: string) =>
  http.del(`/2go/fund/deposits/${encodeURIComponent(id)}`, undefined, quiet());

export const cancelWithdraw = (id: string) =>
  http.del(`/2go/fund/withdrawals/${encodeURIComponent(id)}`, undefined, quiet());

export const cancelExchange = (id: string) =>
  http.del(`/2go/currencyExchanges/${encodeURIComponent(id)}`, undefined, quiet());

export const cancelTransfer = (id: string) =>
  http.del(`/2go/fund/transfers/${encodeURIComponent(id)}`, undefined, quiet());

export const canCancelKind = (kind: RecordKind) =>
  kind === 'deposit' || kind === 'withdraw' || kind === 'exchange' || kind === 'transfer';

/** 列表/详情统一：类型可撤 + allowCancel；详情缺字段时回退 query.allowCancel=1 */
export const canShowCancel = (kind: RecordKind, row: any, queryAllow?: unknown) => {
  if (!canCancelKind(kind) || !row) return false;
  const flag = allowCancelFlag(row);
  if (flag != null) return flag;
  return String(queryAllow ?? '').trim() === '1';
};

export const cancelRecord = (kind: RecordKind, id: string) => {
  if (kind === 'deposit') return cancelDeposit(id);
  if (kind === 'withdraw') return cancelWithdraw(id);
  if (kind === 'exchange') return cancelExchange(id);
  if (kind === 'transfer') return cancelTransfer(id);
  return Promise.reject(new Error(`cancel unsupported for ${kind}`));
};

export const cancelConfirmText = (kind: RecordKind) => {
  if (kind === 'withdraw') return i18n.FundRecords.CancelWithdrawConfirm;
  if (kind === 'exchange') return i18n.FundRecords.CancelExchangeConfirm;
  if (kind === 'transfer') return i18n.FundRecords.CancelTransferConfirm;
  return i18n.FundRecords.CancelApplyConfirm;
};

export type DetailRow = {
  label: string;
  value?: string;
  proofs?: boolean;
  status?: boolean;
  amount?: { text: string; currency: string };
};

export const detailHeroOf = (kind: RecordKind, row: any) => {
  if (kind === 'exchange') {
    const currency = String(row?.toCurrency || '').trim();
    return {
      label: replaceTemplate(i18n.FundRecords.HeroExchangeIn, { currency: currencyFundName(currency) }),
      amount: moneyText(row?.toAmount),
    };
  }
  const currency = String(row?.currency || '').trim();
  const name = currencyFundName(currency);
  if (kind === 'withdraw') {
    return { label: replaceTemplate(i18n.FundRecords.HeroWithdraw, { currency: name }), amount: moneyText(row?.amount) };
  }
  if (kind === 'transfer') {
    return { label: replaceTemplate(i18n.FundRecords.HeroTransfer, { currency: name }), amount: moneyText(row?.amount) };
  }
  return { label: replaceTemplate(i18n.FundRecords.HeroDeposit, { currency: name }), amount: moneyText(row?.amount) };
};

export const detailRowsOf = (kind: RecordKind, row: any, id: string, proofs: boolean): DetailRow[] => {
  const reason = rejectReasonOf(row);
  const status = depositStatus(row?.status);
  if (kind === 'withdraw') {
    const currency = String(row?.currency || '').trim();
    const items: DetailRow[] = [
      { label: i18n.FundRecords.LabelOrderNo, value: String(row?.withdrawalID ?? id) },
      { label: i18n.FundRecords.LabelSubmitTime, value: detailTimeText(row?.submitTime) },
      { label: i18n.FundRecords.LabelPayeeBank, value: bankLine(row?.payeeBankCode, row?.payeeBankName, row?.payeeBankAccountNum, false) },
      {
        label: i18n.H5Withdraw.LabelWithdrawAccount,
        value: replaceTemplate(i18n.FundRecords.LabelWithdrawAccountCash, {
          num: String(row?.accountNum || '').trim(),
        }),
      },
      { label: i18n.FundRecords.LabelWithdrawFunds, value: `${moneyText(row?.amount)} ${currency}`.trim() },
      { label: i18n.FundRecords.LabelStatus, value: status.text, status: true },
    ];
    if (reason) items.push({ label: i18n.FundRecords.LabelRejectReason, value: reason });
    return items;
  }
  if (kind === 'exchange') {
    const rate = row?.exchangeRate ?? row?.rate;
    const items: DetailRow[] = [
      { label: i18n.FundRecords.LabelOrderNo, value: String(row?.exchangeID ?? id) },
      { label: i18n.FundRecords.LabelSubmitTime, value: detailTimeText(row?.submitTime) },
      {
        label: i18n.FundRecords.LabelFrom,
        amount: { text: moneyText(row?.fromAmount), currency: String(row?.fromCurrency || '').trim() },
      },
      {
        label: i18n.FundRecords.LabelTo,
        amount: { text: moneyText(row?.toAmount), currency: String(row?.toCurrency || '').trim() },
      },
      {
        label: i18n.FundRecords.LabelRate,
        value:
          rate != null && rate !== ''
            ? `1 ${String(row?.fromCurrency || '').trim()} ≈ ${rate} ${String(row?.toCurrency || '').trim()}`
            : '—',
      },
      { label: i18n.FundRecords.LabelAccount, value: String(row?.accountNum || '').trim() || '—' },
      { label: i18n.FundRecords.LabelStatus, value: status.text, status: true },
    ];
    if (reason) items.push({ label: i18n.FundRecords.LabelRejectReason, value: reason });
    return items;
  }
  if (kind === 'transfer') {
    const currency = String(row?.currency || '').trim();
    const items: DetailRow[] = [
      { label: i18n.FundRecords.LabelOrderNo, value: String(row?.transferID ?? id) },
      { label: i18n.FundRecords.LabelSubmitTime, value: detailTimeText(row?.submitTime) },
      {
        label: i18n.FundRecords.LabelFromAccount,
        value: accountSide(row?.fromAccount, row?.fromAccountType, row?.fromAccountCapitalType),
      },
      {
        label: i18n.FundRecords.LabelToAccount,
        value: accountSide(row?.toAccount, row?.toAccountType, row?.toAccountCapitalType),
      },
      { label: i18n.FundRecords.LabelTransferFunds, value: `${moneyText(row?.amount)} ${currency}`.trim() },
      { label: i18n.FundRecords.LabelStatus, value: status.text, status: true },
    ];
    if (reason) items.push({ label: i18n.FundRecords.LabelRejectReason, value: reason });
    return items;
  }
  const currency = String(row?.currency || '').trim();
  const items: DetailRow[] = [
    { label: i18n.FundRecords.LabelOrderNo, value: String(row?.depositID ?? id) },
    { label: i18n.FundRecords.LabelSubmitTime, value: detailTimeText(row?.submitTime) },
    { label: i18n.FundRecords.LabelPayerBank, value: bankLine(row?.payerBankCode, row?.payerBankName, row?.payerBankAccountNum, true) },
    { label: i18n.FundRecords.LabelPayeeBank, value: bankLine(row?.payeeBankCode, row?.payeeBankName, row?.payeeBankAccountNum, false) },
    { label: i18n.FundRecords.LabelTransferMethod, value: methodText(row?.method) },
    {
      label: i18n.H5Deposit.LabelDepositAccount,
      value: replaceTemplate(i18n.FundRecords.LabelDepositAccountCash, {
        num: String(row?.accountNum || '').trim(),
      }),
    },
    { label: i18n.FundRecords.LabelDepositFunds, value: `${moneyText(row?.amount)} ${currency}`.trim() },
    { label: i18n.FundRecords.LabelStatus, value: status.text, status: true },
  ];
  if (reason) items.push({ label: i18n.FundRecords.LabelRejectReason, value: reason });
  if (proofs) items.push({ label: i18n.FundRecords.LabelProof, proofs: true });
  const remark = nonempty(row?.remark);
  if (remark) {
    items.push({
      label: i18n.FundRecords.LabelRemark || '备注',
      value: remark,
    });
  }
  return items;
};

export const ensureMarketBanks = async () => {
  if (getMarketBankCache().length) return getMarketBankCache();
  const list = unwrapList(await http.get('/2go/market/banks?limit=100&offset=0', quiet()));
  if (list.length) setMarketBankCache(list);
  return getMarketBankCache();
};

/** 凭证预览：敏感路径走 /file/content→blob，其余拼 VITE_IMG_BASE_URL */
export const loadVoucherUrl = (path: string) => getAuthFileBlobUrl(path);
